import { useState } from 'react';
import { todayIn } from '@njpos/core';
import { api, loadSession, usd } from '../lib/api';
import { ErrorNote, Modal, useLoad } from '../components/ui';

const URG: Record<string, string> = { ok: '', info: 'amber', warning: 'warn', critical: 'bad' };
const RIP: Record<string, [string, string]> = {
  awaiting_payment: ['Pay invoice first', ''], expected: ['Expected', 'amber'], received: ['Received', 'good'], overdue: ['Overdue', 'bad'], disputed: ['Disputed', 'warn'],
};

export default function Bills() {
  const [tab, setTab] = useState<'open' | 'paid'>('open');
  const bills = useLoad(() => api<any[]>(`/api/bills?status=${tab}`), [tab]);
  const rips = useLoad(() => api<any[]>('/api/rips'), []);
  const [paying, setPaying] = useState<any>(null);
  const [receiving, setReceiving] = useState<any>(null);
  const isOwner = loadSession()?.user.role === 'owner';
  const ripOpen = (rips.data ?? []).filter((r) => r.status !== 'received').reduce((s, r) => s + r.expected_cents, 0);

  return (
    <div className="stack">
      <div className="page-head"><div className="grow"><h1>Bills and RIPs</h1>
        <p>NJ vendors give at most 30 days from delivery. A bill paid late puts the store on C.O.D. with every vendor. RIP checks are due 30 to 90 days after the invoice is paid.</p></div></div>
      <div className="row">
        <button className={tab === 'open' ? 'primary' : ''} onClick={() => setTab('open')}>Open bills</button>
        <button className={tab === 'paid' ? 'primary' : ''} onClick={() => setTab('paid')}>Paid</button>
      </div>
      <ErrorNote error={bills.error} />
      <div className="panel">
        {bills.data?.length === 0 ? <div className="empty">{tab === 'open' ? 'All vendor bills are paid.' : 'No paid bills yet.'}</div> : (
          <table className="grid">
            <thead><tr><th>Vendor</th><th>Invoice</th><th>Delivered</th><th>Due</th><th className="n">Amount</th><th className="n">Balance</th><th /></tr></thead>
            <tbody>{bills.data?.map((b) => (
              <tr key={b.id}>
                <td>{b.wholesaler_name}</td><td>{b.invoice_no}</td><td>{b.delivery_date}</td>
                <td className="nowrap">{b.due_date} {b.urgency && b.urgency.level !== 'ok' && <span className={`chip ${URG[b.urgency.level]}`}>{b.urgency.message}</span>}</td>
                <td className="n">{usd(b.amount_cents)}</td><td className="n">{usd(b.balance_cents)}</td>
                <td>{b.status !== 'paid' && isOwner && <button onClick={() => setPaying(b)}>Record payment</button>}</td>
              </tr>))}</tbody>
          </table>
        )}
      </div>

      <div className="page-head" style={{ marginBottom: 0 }}><div className="grow"><h2>RIP rebate ledger</h2><p>{usd(ripOpen)} still to come. NJ requires retailers to keep this record.</p></div></div>
      <div className="panel">
        {rips.data?.length === 0 ? <div className="empty">No RIPs earned yet.</div> : (
          <table className="grid">
            <thead><tr><th>RIP</th><th>Vendor</th><th>Invoice</th><th>Window</th><th>Status</th><th className="n">Expected</th><th className="n">Received</th><th /></tr></thead>
            <tbody>{rips.data?.map((r) => (
              <tr key={r.id}>
                <td className="nowrap">{r.rip_code}</td><td>{r.wholesaler_name}</td><td className="nowrap">{r.invoice_no}</td>
                <td className="nowrap">{r.window_start ? `${r.window_start} to ${r.window_end}` : '—'}</td>
                <td><span className={`chip ${RIP[r.status]?.[1]}`}>{RIP[r.status]?.[0] ?? r.status}</span></td>
                <td className="n">{usd(r.expected_cents)}</td><td className="n">{r.received_cents != null ? usd(r.received_cents) : ''}</td>
                <td>{(r.status === 'expected' || r.status === 'overdue') && isOwner && <button onClick={() => setReceiving(r)}>Check received</button>}</td>
              </tr>))}</tbody>
          </table>
        )}
      </div>
      {paying && <PayModal bill={paying} onClose={() => setPaying(null)} onDone={() => { setPaying(null); bills.reload(); rips.reload(); }} />}
      {receiving && <ReceiveModal rip={receiving} onClose={() => setReceiving(null)} onDone={() => { setReceiving(null); rips.reload(); }} />}
    </div>
  );
}

function PayModal({ bill, onClose, onDone }: { bill: any; onClose: () => void; onDone: () => void }) {
  const [amount, setAmount] = useState((bill.balance_cents / 100).toFixed(2));
  const [paidOn, setPaidOn] = useState(todayIn());
  const [method, setMethod] = useState('check');
  const [reference, setReference] = useState('');
  const [err, setErr] = useState<unknown>(null);
  return (
    <Modal title={`Pay ${bill.wholesaler_name} #${bill.invoice_no}`} onClose={onClose} footer={<button className="primary" onClick={async () => {
      try { await api(`/api/bills/${bill.id}/payments`, { body: { amountCents: Math.round(Number(amount) * 100), paidOn, method, reference: reference || null } }); onDone(); }
      catch (e) { setErr(e); }
    }}>Record payment</button>}>
      <ErrorNote error={err} />
      <div className="field-row">
        <label>Amount<input className="num-input" inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} /></label>
        <label>Paid on<input type="date" value={paidOn} onChange={(e) => setPaidOn(e.target.value)} /></label>
        <label>Method<select value={method} onChange={(e) => setMethod(e.target.value)}>{['check', 'ach', 'portal', 'cash', 'card'].map((m) => <option key={m}>{m}</option>)}</select></label>
        <label>Check or reference no.<input value={reference} onChange={(e) => setReference(e.target.value)} /></label>
      </div>
      <p className="faint">Paying in full starts the 30–90 day window for any RIP checks on this invoice.</p>
    </Modal>
  );
}

function ReceiveModal({ rip, onClose, onDone }: { rip: any; onClose: () => void; onDone: () => void }) {
  const [amount, setAmount] = useState((rip.expected_cents / 100).toFixed(2));
  const [receivedOn, setReceivedOn] = useState(todayIn());
  const [checkNo, setCheckNo] = useState('');
  const [err, setErr] = useState<unknown>(null);
  return (
    <Modal title={`RIP ${rip.rip_code} check`} onClose={onClose} footer={<button className="primary" onClick={async () => {
      try { await api(`/api/rips/${rip.id}/receive`, { body: { amountCents: Math.round(Number(amount) * 100), receivedOn, checkNo: checkNo || null } }); onDone(); }
      catch (e) { setErr(e); }
    }}>Save</button>}>
      <ErrorNote error={err} />
      <div className="field-row">
        <label>Check amount<input className="num-input" inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} /></label>
        <label>Received on<input type="date" value={receivedOn} onChange={(e) => setReceivedOn(e.target.value)} /></label>
        <label>Check no.<input value={checkNo} onChange={(e) => setCheckNo(e.target.value)} /></label>
      </div>
    </Modal>
  );
}
